import {
  DashboardState,
  ESdChangeReason,
  EUserAccessPart,
  IDevice,
  LeftPanelType,
  SpecialDIIds,
  type IScreenCanvasSize,
  getReportCanvasSize,
  getReportDesignAreaWidth,
  getReportRulerWidth
} from "@kuika/kuika-cl-model";
import { UnitType } from "@kuika/kuika-cl-model/src/models/screen-canvas-sizes";
import { createRef } from "react";
import deviceListJson from "../../../shared/data/device-list.json";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { shouldShowReportCanvasSkeleton } from "../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  LEFT_COLLAPSED_BUFFER_PX,
  PANEL_MARGIN_BUFFER_PX,
  RIGHT_COLLAPSED_BUFFER_PX,
  RIGHT_MIN_WIDTH,
  readLeftPanelWidth,
  readRightPanelWidth
} from "../../../shared/utility/panel-dimensions";
import { CanvasFrame } from "./canvas-frame";
import { DesignResize } from "./design-resize";
import { ElementBandsBar } from "./element-bands-bar";
import { isEmptyCanvasOverlayVisible } from "./empty-canvas-overlay";
import { GuideLine, GuideLineType, IGuideLineData } from "./guide-line";
import "./empty-canvas.scss";
import "./ruler-styles.css";
import { HorizontalRuler } from "./horizontal-ruler";
import { ReportCanvasSkeleton } from "./report-canvas-skeleton";
import { VerticalRuler } from "./vertical-ruler";

declare let window: any;

const MCP_INSPECTOR_REQUEST_EVENT = "kuika-mcp-inspector-request";
const MCP_INSPECTOR_CONTROL_EVENT = "kuika-mcp-inspector-control";

export interface ICanvasState {
  rulerUnit: UnitType;
  scrollOffsetX: number;
  scrollOffsetY: number;
  viewportWidth?: number;
  viewportHeight?: number;
  guideLines: IGuideLineData[];
  selectedGuideLineId: string | null;
  previewGuideLine: IGuideLineData | null;
  leftPanelWidth: number;
  rightPanelWidth: number;
  isInspectorActive: boolean;
  /** Live box of the real canvas page, relative to the overlay root. Measured rather than
   *  recomputed from device settings so the empty-state drop frame can never drift. */
  pageBox: IPageBox | null;
}

export interface IPageBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface ICanvasProps {
  zoomScale: number;
  rulerUnit?: UnitType;
  onRulerUnitChange?: (unit: UnitType) => void;
  onZoomChange?: (scale: number) => void;
}

class Canvas extends PureSdComponent<ICanvasProps & IWithSDProps, ICanvasState> {
  deviceList: IDevice[] = deviceListJson as unknown as IDevice[];

  private scrollFrameEl: HTMLElement | null = null;

  private scrollAttachTimer: number | null = null;

  private attachedScrollElements: HTMLElement[] = [];

  private scrollElX: HTMLElement | null = null;

  private scrollElY: HTMLElement | null = null;

  private dragThrottleTimer: number | null = null;

  constructor(props: ICanvasProps & IWithSDProps) {
    super(props);
    this.state = {
      rulerUnit: this.getInitialRulerUnit(props),
      scrollOffsetX: 0,
      scrollOffsetY: 0,
      viewportWidth: undefined,
      viewportHeight: undefined,
      guideLines: this.loadGuideLines(props.sd.sdId),
      selectedGuideLineId: null,
      previewGuideLine: null,
      leftPanelWidth: 238,
      rightPanelWidth: RIGHT_MIN_WIDTH,
      isInspectorActive: false,
      pageBox: null
    };
    if (window.kuika === undefined) {
      window.kuika = {};
    }
    window.kuika.focusToFrame = KMainFunctions.focusToFrame;
  }

  componentDidMount(): void {
    this.attachScrollListener();
    this.attachKeyListeners();
    this.attachIframeClickListener();
    window.addEventListener(MCP_INSPECTOR_REQUEST_EVENT, this.handleInspectorRequest as EventListener);
    document.addEventListener("dragstart", this.handleCanvasDragStart, true);
    document.addEventListener("dragend", this.handleCanvasDragEnd, true);
    document.addEventListener("drop", this.handleCanvasDragEnd, true);
    // Listen for changes to the CSS custom property
    this.updateLeftPanelWidth();
    // Set up a MutationObserver to watch for style changes
    this.observeLeftPanelWidth();
    this.startPageBoxTracking();
  }

  // ── Empty-state drop frame geometry ──
  // The frame must outline the page the user actually drops onto. Device width/height are
  // CSS strings (calc/%/px) resolved by layout, so re-deriving them here would drift; we
  // measure the rendered page instead and mirror its box.
  private overlayRootRef = createRef<HTMLDivElement>();
  private pageBoxObserver?: ResizeObserver;

  /** The white page itself — the drop surface — not the design area around it (which also
   *  carries padding and the device bezel). */
  private getPageElement = (): HTMLElement | null =>
    document.getElementById("kuika_iframeAgent_canvas") ??
    (document.querySelector(".kuika_iframeWrapper") as HTMLElement | null);

  private startPageBoxTracking = (): void => {
    this.measurePageBox();
    window.addEventListener("resize", this.measurePageBox);
    if (typeof ResizeObserver === "undefined") return;
    this.pageBoxObserver = new ResizeObserver(this.measurePageBox);
    const root = this.overlayRootRef.current;
    const page = this.getPageElement();
    if (root) this.pageBoxObserver.observe(root);
    if (page) this.pageBoxObserver.observe(page);
  };

  private stopPageBoxTracking = (): void => {
    window.removeEventListener("resize", this.measurePageBox);
    this.pageBoxObserver?.disconnect();
    this.pageBoxObserver = undefined;
  };

  private measurePageBox = (): void => {
    const root = this.overlayRootRef.current;
    const page = this.getPageElement();
    if (!root || !page) return;
    const rootRect: DOMRect = root.getBoundingClientRect();
    const pageRect: DOMRect = page.getBoundingClientRect();
    const next: IPageBox = {
      top: Math.round(pageRect.top - rootRect.top),
      left: Math.round(pageRect.left - rootRect.left),
      width: Math.round(pageRect.width),
      height: Math.round(pageRect.height)
    };
    const prev: IPageBox | null = this.state.pageBox;
    if (
      prev &&
      prev.top === next.top &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.height === next.height
    ) {
      return;
    }
    this.setState({ pageBox: next });
  };

  componentDidUpdate(prevProps: ICanvasProps & IWithSDProps) {
    const prevScreen = prevProps.sd.project?.screenCanvasSizes?.screens?.find((x) => x.id === prevProps.sd.sdId);
    const currentScreen = this.props.sd.project?.screenCanvasSizes?.screens?.find((x) => x.id === this.props.sd.sdId);

    const prevUnit = prevScreen?.reportPaper?.unit;
    const currentUnit = currentScreen?.reportPaper?.unit;

    const newUnit = this.getInitialRulerUnit(this.props);

    if (prevUnit !== currentUnit || newUnit !== this.state.rulerUnit) {
      this.setState({ rulerUnit: newUnit });
    }

    if (this.props.rulerUnit && this.props.rulerUnit !== this.state.rulerUnit) {
      this.setState({ rulerUnit: this.props.rulerUnit });
      if (this.props.onRulerUnitChange) {
        this.props.onRulerUnitChange(this.props.rulerUnit);
      }
    }

    if (prevProps.sd.sdId !== this.props.sd.sdId) {
      this.setState({ guideLines: this.loadGuideLines(this.props.sd.sdId) });
    }

    const prevGuideLines = prevScreen?.guideLines;
    const currentGuideLines = currentScreen?.guideLines;
    if (prevGuideLines !== currentGuideLines && JSON.stringify(prevGuideLines) !== JSON.stringify(currentGuideLines)) {
      this.setState({ guideLines: this.loadGuideLines(this.props.sd.sdId) });
    }

    // Device switches and panel resizes re-render without resizing the observed nodes in
    // every case, so re-measure here too; measurePageBox no-ops when nothing moved.
    this.measurePageBox();
  }

  componentWillUnmount(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.detachScrollListeners();
    this.detachKeyListeners();
    document.removeEventListener("dragstart", this.handleCanvasDragStart, true);
    document.removeEventListener("dragend", this.handleCanvasDragEnd, true);
    document.removeEventListener("drop", this.handleCanvasDragEnd, true);
    document.body.classList.remove("kuika_canvas_dragging");
    this.stopPageBoxTracking();

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.removeEventListener("click", this.handleIframeClick as any);
    }
    this.iframeClickAttached = false;

    if (this.scrollAttachTimer) {
      window.clearInterval(this.scrollAttachTimer);
      this.scrollAttachTimer = null;
    }
    if (this.dragThrottleTimer !== null) {
      window.clearTimeout(this.dragThrottleTimer);
      this.dragThrottleTimer = null;
    }
    window.removeEventListener(MCP_INSPECTOR_REQUEST_EVENT, this.handleInspectorRequest as EventListener);
  }

  attachKeyListeners = (): void => {
    document.addEventListener("keydown", this.handleKeyDown);

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.addEventListener("keydown", this.handleKeyDown as any);
    }
  };

  detachKeyListeners = (): void => {
    document.removeEventListener("keydown", this.handleKeyDown);

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.removeEventListener("keydown", this.handleKeyDown as any);
    }
  };

  private iframeClickAttached = false;

  attachIframeClickListener = (): void => {
    const tryAttach = () => {
      if (this.iframeClickAttached) return;

      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.removeEventListener("click", this.handleIframeClick as any);
        iframeDoc.addEventListener("click", this.handleIframeClick as any);
        this.iframeClickAttached = true;
        if (this.state.isInspectorActive) {
          this.dispatchInspectorControl(true);
        }
      }
    };

    tryAttach();
    setTimeout(tryAttach, 500);
    setTimeout(tryAttach, 1000);
    setTimeout(tryAttach, 2000);
  };

  handleIframeClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (!target.closest) return;

    if (!target.closest(".guide-line")) {
      this.setState({ selectedGuideLineId: null });
    }
  };

  dispatchInspectorControl = (active: boolean): void => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
    const frameWindow = iframe?.contentWindow;
    if (!frameWindow) return;

    const FrameCustomEvent = (frameWindow as unknown as { CustomEvent: typeof CustomEvent }).CustomEvent;
    frameWindow.dispatchEvent(new FrameCustomEvent(MCP_INSPECTOR_CONTROL_EVENT, { detail: { active } }));
  };

  handleInspectorRequest = (event: Event): void => {
    const active = Boolean((event as CustomEvent<{ active?: boolean }>).detail?.active);
    this.setState({ isInspectorActive: active });
    this.dispatchInspectorControl(active);
  };

  loadGuideLines = (screenId: string): IGuideLineData[] => {
    const { project } = this.props.sd;
    const screen = project?.screenCanvasSizes?.screens?.find((s) => s.id === screenId);
    const guideLines = screen?.guideLines || [];
    return guideLines.map((gl) => ({
      id: gl.id,
      type: gl.type as GuideLineType,
      position: gl.position
    }));
  };

  saveGuideLines = (guideLines: IGuideLineData[]): void => {
    const { project, updateProject, sdId } = this.props.sd;

    const { screenCanvasSizes } = project;
    if (!screenCanvasSizes) {
      project.screenCanvasSizes = { screens: [] };
    }

    if (!screenCanvasSizes.screens) {
      screenCanvasSizes.screens = [];
    }

    const guideLinesForSave = guideLines.map((gl) => ({
      id: gl.id,
      type: gl.type.toString(),
      position: gl.position
    }));

    const screen = screenCanvasSizes.screens.find((s) => s.id === sdId);
    const canvasSizeByScreen: IScreenCanvasSize = {
      id: sdId,
      guideLines: guideLinesForSave
    };

    if (screen) {
      screen.guideLines = guideLinesForSave;
    } else {
      screenCanvasSizes.screens.push(canvasSizeByScreen);
    }

    updateProject({ ...project, screenCanvasSizes });
  };

  handleCreateGuideLine = (position: number, type: GuideLineType): void => {
    const newGuideLine: IGuideLineData = {
      id: `guide-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      position
    };

    this.setState(
      (prevState) => ({
        guideLines: [...prevState.guideLines, newGuideLine],
        previewGuideLine: null
      }),
      () => {
        this.saveGuideLines(this.state.guideLines);
      }
    );
  };

  handlePreviewGuideLine = (position: number, type: GuideLineType): void => {
    const previewGuideLine: IGuideLineData = {
      id: "preview",
      type,
      position
    };
    this.setState({ previewGuideLine });
  };

  handleCancelPreviewGuideLine = (): void => {
    this.setState({ previewGuideLine: null });
  };

  handleDragGuideLine = (id: string, position: number): void => {
    this.setState((prevState) => ({
      guideLines: prevState.guideLines.map((line) => (line.id === id ? { ...line, position } : line))
    }));

    if (this.dragThrottleTimer !== null) {
      window.clearTimeout(this.dragThrottleTimer);
    }

    this.dragThrottleTimer = window.setTimeout(() => {
      this.saveGuideLines(this.state.guideLines);
      this.dragThrottleTimer = null;
    }, 300);
  };

  handleRemoveGuideLine = (id: string): void => {
    this.setState(
      (prevState) => ({
        guideLines: prevState.guideLines.filter((line) => line.id !== id),
        selectedGuideLineId: prevState.selectedGuideLineId === id ? null : prevState.selectedGuideLineId
      }),
      () => {
        this.saveGuideLines(this.state.guideLines);
      }
    );
  };

  handleSelectGuideLine = (id: string): void => {
    this.setState((prevState) => ({
      selectedGuideLineId: prevState.selectedGuideLineId === id ? null : id
    }));
  };

  handleKeyDown = (e: KeyboardEvent): void => {
    if ((e.key === "Delete" || e.key === "Backspace") && this.state.selectedGuideLineId) {
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA" ||
        activeElement?.getAttribute("contenteditable") === "true";

      if (!isInputFocused) {
        e.preventDefault();
        this.handleRemoveGuideLine(this.state.selectedGuideLineId);
      }
    }
  };

  handleCanvasWrapperClick = (e: React.MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (!target.closest(".guide-line")) {
      this.setState({ selectedGuideLineId: null });
    }
  };

  getInitialRulerUnit = (props: ICanvasProps & IWithSDProps): UnitType => {
    const { screenCanvasSizes } = props.sd.project;
    if (screenCanvasSizes) {
      const screen = screenCanvasSizes.screens?.find((x) => x.id === props.sd.sdId);
      if (screen && screen.reportPaper) {
        if (screen.reportPaper.unit) {
          const paperUnit = screen.reportPaper.unit;
          if (paperUnit === "mm") return UnitType.MM;
          if (paperUnit === "inch") return UnitType.INCH;
          if (paperUnit === "pt") return UnitType.PT;
          return UnitType.PX;
        }
      }
    }
    return props.rulerUnit || UnitType.MM;
  };

  attachScrollListener = () => {
    const tryAttach = () => {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      const primary = doc?.getElementById("kuika_scrollFrame") as HTMLElement | null;
      const secondary = doc?.getElementById("kuika_fullDiv") as HTMLElement | null;
      const reportWrapper = (doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement) || null;

      const candidates: HTMLElement[] = [];
      if (primary) candidates.push(primary);
      if (secondary) candidates.push(secondary);
      if (reportWrapper) candidates.push(reportWrapper);

      if (candidates.length > 0) {
        const known = new Set(this.attachedScrollElements);
        candidates.forEach((el) => {
          if (!known.has(el)) {
            el.addEventListener("scroll", this.handleExternalScroll, { passive: true });
            this.attachedScrollElements.push(el);
          }
        });
        this.scrollFrameEl = this.attachedScrollElements[0] || null;
        this.updateOffsetsFromScrollFrame();
      }
    };
    tryAttach();
    if (!this.scrollAttachTimer) {
      this.scrollAttachTimer = window.setInterval(tryAttach, 500);
    }
  };

  detachScrollListeners = () => {
    if (this.attachedScrollElements.length > 0) {
      this.attachedScrollElements.forEach((el) => el.removeEventListener("scroll", this.handleExternalScroll as any));
      this.attachedScrollElements = [];
    }
  };

  updateOffsetsFromScrollFrame = () => {
    // Re-evaluate each time in case the active scroller changes
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    const primary = doc?.getElementById("kuika_scrollFrame") as HTMLElement | null;
    const secondary = doc?.getElementById("kuika_fullDiv") as HTMLElement | null;
    const reportWrapper = (doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement) || null;

    const candidates: HTMLElement[] = [];
    if (primary) candidates.push(primary);
    if (secondary) candidates.push(secondary);
    if (reportWrapper) candidates.push(reportWrapper);

    const pickHorizontal = (els: HTMLElement[]) =>
      els.map((el) => ({ el, diff: (el.scrollWidth || 0) - (el.clientWidth || 0) })).sort((a, b) => b.diff - a.diff)[0]
        ?.el ||
      els[0] ||
      null;
    const pickVertical = (els: HTMLElement[]) =>
      els
        .map((el) => ({ el, diff: (el.scrollHeight || 0) - (el.clientHeight || 0) }))
        .sort((a, b) => b.diff - a.diff)[0]?.el ||
      els[0] ||
      null;

    this.scrollElX = pickHorizontal(candidates);
    this.scrollElY = pickVertical(candidates);

    const x = this.scrollElX ? this.scrollElX.scrollLeft || 0 : 0;
    const y = this.scrollElY ? this.scrollElY.scrollTop || 0 : 0;
    const viewportWidth = (this.scrollElX?.clientWidth || this.scrollElY?.clientWidth || 0) + 4 || undefined;
    const viewportHeight = this.scrollElY?.clientHeight || this.scrollElX?.clientHeight || undefined;

    if (
      x !== this.state.scrollOffsetX ||
      y !== this.state.scrollOffsetY ||
      viewportWidth !== this.state.viewportWidth ||
      viewportHeight !== this.state.viewportHeight
    ) {
      this.setState({ scrollOffsetX: x, scrollOffsetY: y, viewportWidth, viewportHeight });
    }
  };

  handleExternalScroll = () => {
    this.updateOffsetsFromScrollFrame();
  };

  observer: MutationObserver | null = null;

  observeLeftPanelWidth = () => {
    this.observer = new MutationObserver(() => {
      this.updateLeftPanelWidth();
    });

    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"]
    });
  };

  updateLeftPanelWidth = () => {
    const leftPanelWidth = readLeftPanelWidth();
    const rightPanelWidth = readRightPanelWidth();
    if (leftPanelWidth !== this.state.leftPanelWidth || rightPanelWidth !== this.state.rightPanelWidth) {
      this.setState({ leftPanelWidth, rightPanelWidth });
    }
  };

  getZoomScale = () => {
    if (
      this.props.sd.dashboardState !== DashboardState.design &&
      this.props.sd.dashboardState !== DashboardState.reportDesigner
    ) {
      return 1;
    }
    return this.props.zoomScale;
  };

  getDeviceWidth = (selectedDevice: IDevice) => {
    const leftPanelWidth = this.state.leftPanelWidth;
    const rightPanelWidth = this.state.rightPanelWidth;
    let width: string = `calc(100vw - ${leftPanelWidth + rightPanelWidth + PANEL_MARGIN_BUFFER_PX}px)`;

    if (selectedDevice && selectedDevice.viewport && selectedDevice.viewport === "none") {
      if (
        this.props.sd.dashboardState !== DashboardState.design &&
        this.props.sd.dashboardState !== DashboardState.reportDesigner
      ) {
        width = "100vw";
      }
    } else if (selectedDevice && selectedDevice.viewport && selectedDevice.viewport.indexOf("x") > -1) {
      width = `${parseInt(selectedDevice.viewport.substring(0, selectedDevice.viewport.indexOf("x"))) + 24}px`;
    }
    const { screenCanvasSizes } = this.props.sd.project;
    let screens: IScreenCanvasSize[] | undefined = [];
    if (screenCanvasSizes) {
      screens = screenCanvasSizes.screens;
    }
    const screen = screens?.find((x) => x.id === this.props.sd.sdId);
    if (
      this.props.sd.dashboardState === DashboardState.design &&
      (screenCanvasSizes?.width || (screen && screen.width)) &&
      selectedDevice.viewport === "custom"
    ) {
      const screenCanvasSize = screenCanvasSizes?.screens?.find((x) => x.id === this.props.sd.sdId);
      if (screenCanvasSize && screenCanvasSize.width) {
        width = `${screenCanvasSize.width + 24}px`;
      } else if (screenCanvasSizes?.width) {
        width = `${screenCanvasSizes.width + 24}px`;
      }
    }
    if (this.props.sd.dashboardState === DashboardState.reportDesigner) {
      width = `${getReportDesignAreaWidth(screen)}px`;
    }
    return width;
  };

  getDeviceHeight = (selectedDevice: IDevice) => {
    let height: string = `${70 / this.getZoomScale()}%`;
    if (
      (selectedDevice && selectedDevice.viewport && selectedDevice.viewport === "none") ||
      this.props.sd.dashboardState === DashboardState.design
    ) {
      height = `calc(${100 / this.getZoomScale()}%)`;
    } else if (selectedDevice && selectedDevice.viewport && selectedDevice.viewport.indexOf("x") > -1) {
      height = `${
        parseInt(selectedDevice.viewport.substring(selectedDevice.viewport.indexOf("x") + 1)) / this.getZoomScale()
      }px`;
    }
    if (this.props.sd.dashboardState === DashboardState.reportDesigner) {
      height = `${getReportCanvasSize(this.getReportCanvasSizeEntry()).height}px`;
    }
    return height;
  };

  /** This screen's canvas-size record — the one input every report measurement derives from. */
  getReportCanvasSizeEntry = (): IScreenCanvasSize | undefined => {
    return this.props.sd.project?.screenCanvasSizes?.screens?.find(
      (screen: IScreenCanvasSize): boolean => screen.id === this.props.sd.sdId
    );
  };

  getDevicePaddingLeft = () => {
    const leftPanelWidth = this.state.leftPanelWidth;
    return leftPanelWidth - 224;
  };

  // The overlay may only cover a genuinely blank iframe — see isEmptyCanvasOverlayVisible
  // for the two things (layout section DIs, a bound master screen) that make a
  // root-empty screen non-blank. masterSd is read rather than the configured masterId
  // because it is exactly what frame-provider hands the iframe as masterScreenDesign; it
  // defaults to {} so the id, not the object, is the test.
  shouldShowEmptyCanvasOverlay = (): boolean => {
    return isEmptyCanvasOverlayVisible({
      dashboardState: this.props.sd.dashboardState,
      displayItems: this.props.sd.getScreenDesign()?.displayItems,
      masterScreenDesignId: this.props.sd.masterSd?.id?.toString()
    });
  };

  // Toggles the empty-canvas overlay's drag state. The drag originates from the
  // left panel in THIS (designer) frame, so dragstart/dragend fire reliably here
  // even though the drop itself lands inside the canvas iframe. A body class is
  // used (not React state) so it survives canvas re-renders during the drag.
  handleCanvasDragStart = (): void => {
    document.body.classList.add("kuika_canvas_dragging");
  };

  handleCanvasDragEnd = (): void => {
    document.body.classList.remove("kuika_canvas_dragging");
  };

  getIframeCss = () => {
    if (this.props.sd.dashboardState === DashboardState.readOnly) {
      return "kuika_iframeReadOnlyWrapper";
    }
    return "kuika_iframeDesignWrapper";
  };

  unSelectDevice = () => {
    if (this.props.sd && this.props.sd.selectedDevice && this.props.sd.selectedDevice.id !== "") {
      if (this.props.sd && this.props.sd.setSpecificDevice) {
        this.props.sd.setSpecificDevice(this.deviceList[0], false);
      }
    }
  };

  shouldComponentUpdate = (nextProps, nextState) => {
    if (
      this.props &&
      this.props.sd &&
      this.props.sd.selectedDevice &&
      this.props.sd.selectedDevice.css !== "" &&
      nextProps &&
      nextProps.sd &&
      nextProps.sd.selectedDevice &&
      nextProps.sd.selectedDevice.css === ""
    ) {
      return false;
    }
    return true;
  };

  getClassNameForDesignArea = (selectedDevice): string => {
    if (this.props.sd.dashboardState === DashboardState.readOnly) {
      return `kuika_designArea kuika_device ${selectedDevice.css}`;
    }
    if (this.props.sd.dashboardState === DashboardState.reportDesigner) {
      return `kuika_designArea kuika_designArea_reportDesigner`;
    }
    return `kuika_designArea`;
  };

  getKuikaIframeWrapperClassName = () => {
    const res =
      this.props.sd.dashboardState === DashboardState.design ||
      this.props.sd.dashboardState === DashboardState.reportDesigner
        ? "kuika_iframeWrapper"
        : "screen kuika_iframeWrapper";
    return res;
  };

  waitAndResize = (event?: any) => {
    const display =
      this.props.sd.dashboardState === DashboardState.design ||
      this.props.sd.dashboardState === DashboardState.reportDesigner;
    KMainFunctions.waitAndResize(display, this.props.sd.toolbarIsHidden, this.props.sd, this.getZoomScale());
  };

  handleWrapperClick = () => {
    if (
      this.props.sd.dashboardState === DashboardState.design ||
      this.props.sd.dashboardState === DashboardState.reportDesigner
    ) {
      this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
      const hasUserAccessCreateComponent = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
        EUserAccessPart.CreateComponent,
        this.props.sd.project?.projectAccess
      );
      if (this.props.sd.getActiveLeftPanel() === LeftPanelType.Screens && hasUserAccessCreateComponent) {
        this.props.sd.setActiveLeftPanelByClick(LeftPanelType.Component);
      }
    }
  };

  render() {
    let selectedDevice: IDevice = this.deviceList[0];
    if (this.props && this.props.sd && this.props.sd.selectedDevice) {
      selectedDevice = this.props.sd.selectedDevice;
    }
    let minWidth = 320;
    if (this.props && this.props.sd) {
      const sd = this.props.sd.getScreenDesign();
      if (sd && sd.devices) {
        if (sd.devices.desktop === true) {
          minWidth = 800;
        }
        if (sd.devices.tablet === true) {
          minWidth = 600;
        }
        if (sd.devices.phone === true) {
          minWidth = 320;
        }
      }
    }
    const leftPanelWidth = this.state.leftPanelWidth;
    let maxWidth = `calc(100vw - ${leftPanelWidth + this.state.rightPanelWidth + PANEL_MARGIN_BUFFER_PX}px)`;
    if (
      this.props.sd.isLeftPanelCollapsed[this.props.sd.dashboardState] &&
      this.props.sd.isRightPanelCollapsed[this.props.sd.dashboardState]
    ) {
      maxWidth = `calc(100vw - 100px)`;
    } else if (this.props.sd.isLeftPanelCollapsed[this.props.sd.dashboardState]) {
      maxWidth = `calc(100vw - ${this.state.rightPanelWidth + LEFT_COLLAPSED_BUFFER_PX}px)`;
    } else if (this.props.sd.isRightPanelCollapsed[this.props.sd.dashboardState]) {
      maxWidth = `calc(100vw - ${leftPanelWidth + RIGHT_COLLAPSED_BUFFER_PX}px)`;
    }
    let minHeight = 150;
    const isReportDesigner = this.props.sd.dashboardState === DashboardState.reportDesigner;
    // The rulers measure the same page the design area is sized for.
    const reportCanvasSizeEntry: IScreenCanvasSize | undefined = this.getReportCanvasSizeEntry();
    const rulerWidth: number = getReportRulerWidth(reportCanvasSizeEntry);
    const rulerHeight: number = getReportCanvasSize(reportCanvasSizeEntry).height;

    const pageBreakPositions: number[] = [];
    if (isReportDesigner) {
      const sd = this.props.sd.getScreenDesign();
      const rootDI = sd?.displayItems?.find((di) => di.id === SpecialDIIds.rootDI);
      const collectPageBreaks = (displayItem: any) => {
        if (displayItem?.item?.name === "Page Break" && displayItem.styleObj?.position?.yAxis !== undefined) {
          pageBreakPositions.push(displayItem.styleObj.position.yAxis);
        }
        if (displayItem?.children && displayItem.children.length > 0) {
          displayItem.children.forEach((child: any) => collectPageBreaks(child));
        }
      };
      if (rootDI) {
        collectPageBreaks(rootDI);
      }
    }

    return (
      <div
        ref={this.overlayRootRef}
        style={{
          position: "relative"
        }}
      >
        {/* <DevTools></DevTools> */}
        <div
          style={
            this.props.sd.dashboardState !== DashboardState.design &&
            this.props.sd.dashboardState !== DashboardState.reportDesigner
              ? { display: "none" }
              : { height: "100%" }
          }
        >
          <div className={this.getIframeCss()} onScroll={this.waitAndResize} onClick={this.handleWrapperClick}>
            <div
              id="kuika_designArea"
              className={this.getClassNameForDesignArea(selectedDevice)}
              style={{
                width: this.getDeviceWidth(selectedDevice),
                height: this.getDeviceHeight(selectedDevice),
                maxWidth,
                minWidth: this.props.sd.dashboardState === DashboardState.reportDesigner ? 0 : "none"
              }}
            >
              <div className="inner"></div>
              <div className="speaker"></div>
              <div className="sensors"></div>
              <div className="more-sensors"></div>
              <div className="camera"></div>
              <div className="notch">
                <div className="camera"></div>
                <div className="speaker"></div>
              </div>
              <div className="top-bar"></div>
              <div className="sleep"></div>
              <div className="bottom-bar"></div>
              <div className="volume"></div>
              <div className="overflow">
                <div className="shadow"></div>
                <div className="shadow shadow--tr"></div>
                <div className="shadow shadow--tl"></div>
                <div className="shadow shadow--br"></div>
                <div className="shadow shadow--bl"></div>
              </div>
              <div className="inner-shadow"></div>
              <div className={this.getKuikaIframeWrapperClassName()}>
                {shouldShowReportCanvasSkeleton(this.props.sd) && (
                  <ReportCanvasSkeleton screenCanvasSize={reportCanvasSizeEntry} />
                )}
                {isReportDesigner && (
                  <div className="ruler-container">
                    <div className="ruler-row">
                      <div className="ruler-corner"></div>
                      <HorizontalRuler
                        key={`h-ruler-${this.state.rulerUnit}`}
                        width={rulerWidth}
                        zoomScale={this.getZoomScale()}
                        unit={this.state.rulerUnit}
                        offset={this.state.scrollOffsetX}
                        onCreateGuideLine={this.handleCreateGuideLine}
                        onPreviewGuideLine={this.handlePreviewGuideLine}
                        onCancelPreview={this.handleCancelPreviewGuideLine}
                      />
                    </div>
                    <div className="ruler-row">
                      <ElementBandsBar
                        height={rulerHeight}
                        zoomScale={this.getZoomScale()}
                        pageBreakPositions={pageBreakPositions}
                        selectedKuikaIds={this.props.sd.selectedKuikaIds}
                        onSelectElement={(kuikaId) => this.props.sd.setSelectedDisplayItem(kuikaId)}
                      />
                      <VerticalRuler
                        key={`v-ruler-${this.state.rulerUnit}`}
                        height={rulerHeight}
                        zoomScale={this.getZoomScale()}
                        unit={this.state.rulerUnit}
                        offset={this.state.scrollOffsetY}
                        pageBreakPositions={pageBreakPositions}
                        onCreateGuideLine={this.handleCreateGuideLine}
                        onPreviewGuideLine={this.handlePreviewGuideLine}
                        onCancelPreview={this.handleCancelPreviewGuideLine}
                      />
                      <div
                        className={`canvas-with-rulers${this.state.isInspectorActive ? " canvas-with-rulers--inspector-active" : ""}`}
                        onClick={this.handleCanvasWrapperClick}
                      >
                        {this.state.previewGuideLine && (
                          <GuideLine
                            key="preview"
                            guideLine={this.state.previewGuideLine}
                            zoomScale={this.getZoomScale()}
                            unit={this.state.rulerUnit}
                            onDrag={() => {}}
                            onRemove={() => {}}
                            containerWidth={rulerWidth}
                            containerHeight={rulerHeight}
                            isSelected={false}
                            scrollOffsetX={this.state.scrollOffsetX}
                            scrollOffsetY={this.state.scrollOffsetY}
                          />
                        )}
                        {this.state.guideLines.map((guideLine) => (
                          <GuideLine
                            key={guideLine.id}
                            guideLine={guideLine}
                            zoomScale={this.getZoomScale()}
                            unit={this.state.rulerUnit}
                            onDrag={this.handleDragGuideLine}
                            onRemove={this.handleRemoveGuideLine}
                            onSelect={this.handleSelectGuideLine}
                            isSelected={this.state.selectedGuideLineId === guideLine.id}
                            containerWidth={rulerWidth}
                            containerHeight={rulerHeight}
                            scrollOffsetX={this.state.scrollOffsetX}
                            scrollOffsetY={this.state.scrollOffsetY}
                          />
                        ))}
                        {(this.props.sd.dashboardState === DashboardState.design ||
                          this.props.sd.dashboardState === DashboardState.reportDesigner) && (
                          <DesignResize
                            sd={this.props.sd}
                            minWidth={minWidth}
                            minHeight={minHeight}
                            onChangeResize={this.unSelectDevice}
                            zoomScale={this.getZoomScale()}
                          ></DesignResize>
                        )}
                        <CanvasFrame zoomScale={this.props.zoomScale} />
                      </div>
                    </div>
                  </div>
                )}
                {!isReportDesigner && (
                  <div
                    className={`mcp-canvas-shell${this.state.isInspectorActive ? " mcp-canvas-shell--inspector-active" : ""}`}
                  >
                    {(this.props.sd.dashboardState === DashboardState.design ||
                      this.props.sd.dashboardState === DashboardState.reportDesigner) && (
                      <DesignResize
                        sd={this.props.sd}
                        minWidth={minWidth}
                        minHeight={minHeight}
                        onChangeResize={this.unSelectDevice}
                        zoomScale={this.getZoomScale()}
                      ></DesignResize>
                    )}
                    <CanvasFrame zoomScale={this.props.zoomScale} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {this.shouldShowEmptyCanvasOverlay() && (
          <div className="kuika_empty_canvas_overlay" data-testid="emptyCanvasOverlay" aria-hidden="true">
            <div className="kuika_empty_canvas_overlay__dots" />
            {/* Mirrors the measured page box, so the drop target is outlined exactly for
                whichever device is selected. */}
            {this.state.pageBox && (
              <div
                className="kuika_empty_canvas_overlay__page"
                style={{
                  top: this.state.pageBox.top,
                  left: this.state.pageBox.left,
                  width: this.state.pageBox.width,
                  height: this.state.pageBox.height
                }}
              >
                <div className="kuika_empty_canvas_overlay__guide kuika_empty_canvas_overlay__guide--v" />
                <div className="kuika_empty_canvas_overlay__guide kuika_empty_canvas_overlay__guide--h" />
              </div>
            )}
            <div className="kuika_empty_canvas_overlay__center">
              <div className="kuika_empty_canvas_overlay__icon">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                </svg>
              </div>
              <div className="kuika_empty_canvas_overlay__text kuika_empty_canvas_overlay__text--idle">
                <div className="kuika_empty_canvas_overlay__title">Add or drag an element</div>
                <div className="kuika_empty_canvas_overlay__subtitle">Select from the left panel</div>
              </div>
              <div className="kuika_empty_canvas_overlay__text kuika_empty_canvas_overlay__text--drag">
                <div className="kuika_empty_canvas_overlay__title">Drop to add here</div>
                <div className="kuika_empty_canvas_overlay__subtitle">Aligns to page center</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    // The paper size lives on the project, and the design area, the rulers and the frame
    // placeholder are all sized from it here — so the one reason that carries a project change
    // has to get through, or picking A3 leaves an A4-sized frame around an A3 page.
    return changeReason === ESdChangeReason.ChangeProjectStyle;
  };
}

const canvas = withSD<ICanvasProps>(Canvas);
export { canvas as Canvas };
